import React, { useEffect, useMemo, useState } from "react";
import { fetchJson } from "../../lib/api";
import { UNAVAILABLE_STATUSES } from "../../../lib/booking-status";
import { formatAmount, formatStatusLabel, channelLabel } from "../../lib/format-utils";
import {
  addDays,
  formatDate,
  formatShortDate,
  getDatesInRange,
  getDatesInYear,
  getMonthColor,
  getWeekInfo,
  parseUtcDate,
} from "../../lib/date-utils";
import type { BookingResponse, InventoryResponse } from "../../lib/types";
import { useI18n } from "../../i18n/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../table";

export const BookingsView = ({
  houseId,
  search,
  setSearch,
}: {
  houseId: number | null;
  search: string;
  setSearch: (nextSearch: string, options?: { replace?: boolean }) => void;
}) => {
  const { t, locale } = useI18n();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const yearParam = Number.parseInt(searchParams.get("year") ?? "", 10);
  const inventoryChannel = "villavilla";
  const [data, setData] = useState<BookingResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inventory, setInventory] = useState<InventoryResponse | null>(null);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryError, setInventoryError] = useState<string | null>(null);

  useEffect(() => {
    if (!houseId) {
      setData(null);
      return;
    }
    const yearQuery = Number.isNaN(yearParam) ? "" : `&year=${yearParam}`;
    setIsLoading(true);
    setError(null);
    fetchJson<BookingResponse>(`/api/bookings?house=${houseId}${yearQuery}`)
      .then((response) => {
        setData(response);
        if (Number.isNaN(yearParam) || response.year !== yearParam) {
          const nextParams = new URLSearchParams(search);
          nextParams.set("year", String(response.year));
          setSearch(`?${nextParams.toString()}`, { replace: true });
        }
      })
      .catch((fetchError) => {
        setError(
          fetchError instanceof Error
            ? fetchError.message
            : t("bookings.loadFailed", "Failed to load bookings"),
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [houseId, search, setSearch, yearParam]);

  useEffect(() => {
    if (!houseId) {
      setInventory(null);
      return;
    }
    const inventoryYear = Number.isNaN(yearParam)
      ? new Date().getUTCFullYear()
      : yearParam;
    setInventoryLoading(true);
    setInventoryError(null);
    fetchJson<InventoryResponse>(
      `/api/bookings/inventory?house=${houseId}&year=${inventoryYear}&channel=${inventoryChannel}`,
    )
      .then((response) => {
        setInventory(response);
        if (Number.isNaN(yearParam) && response.year !== inventoryYear) {
          const nextParams = new URLSearchParams(search);
          nextParams.set("year", String(response.year));
          setSearch(`?${nextParams.toString()}`, { replace: true });
        }
      })
      .catch((fetchError) => {
        setInventoryError(
          fetchError instanceof Error
            ? fetchError.message
            : t("bookings.inventoryLoadFailed", "Failed to load inventory"),
        );
      })
      .finally(() => {
        setInventoryLoading(false);
      });
  }, [houseId, inventoryChannel, search, setSearch, yearParam]);

  const unavailableDates = useMemo(() => {
    const dates = new Set<string>();
    if (!data) {
      return dates;
    }
    data.bookings.forEach((booking) => {
      if (!booking.status || !UNAVAILABLE_STATUSES.has(booking.status)) {
        return;
      }
      getDatesInRange(booking.startDate, booking.endDate).forEach((date) => {
        dates.add(date);
      });
    });
    return dates;
  }, [data]);

  const weekStartDay = data?.weekStartDay ?? "friday";

  const summaries = useMemo(() => {
    if (!data) {
      return { weeks: [], months: [] } as {
        weeks: {
          key: string;
          label: string;
          startDate: string;
          endDate: string;
          totalDays: number;
          bookedDays: number;
          weekendBooked: number;
          weekdayBooked: number;
          amount: number;
        }[];
        months: {
          month: number;
          label: string;
          totalDays: number;
          bookedDays: number;
          availableDays: number;
          ratio: number;
        }[];
      };
    }
    const weekMap = new Map<
      string,
      {
        week: number;
        weekYear: number;
        startDate: string;
        endDate: string;
        totalDays: number;
        bookedDays: number;
        weekendBooked: number;
        weekdayBooked: number;
        amount: number;
      }
    >();
    const monthMap = new Map<number, { month: number; totalDays: number; bookedDays: number }>();
    const unavailableBookings = data.bookings.filter(
      (booking) => booking.status && UNAVAILABLE_STATUSES.has(booking.status),
    );

    getDatesInYear(data.year).forEach((date) => {
      const dateKey = formatDate(date);
      const isBooked = unavailableDates.has(dateKey);
      const monthIndex = date.getUTCMonth();
      const monthEntry = monthMap.get(monthIndex) ?? {
        month: monthIndex,
        totalDays: 0,
        bookedDays: 0,
      };
      monthEntry.totalDays += 1;
      if (isBooked) {
        monthEntry.bookedDays += 1;
      }
      monthMap.set(monthIndex, monthEntry);

      const { week, weekYear, startDate } = getWeekInfo(date, weekStartDay);
      const weekKey = `${weekYear}-W${String(week).padStart(2, "0")}`;
      const weekEntry = weekMap.get(weekKey) ?? {
        week,
        weekYear,
        startDate: formatDate(startDate),
        endDate: formatDate(addDays(startDate, 7)),
        totalDays: 0,
        bookedDays: 0,
        weekendBooked: 0,
        weekdayBooked: 0,
        amount: 0,
      };
      weekEntry.totalDays += 1;
      if (isBooked) {
        weekEntry.bookedDays += 1;
        const day = date.getUTCDay();
        if (day === 0 || day === 5 || day === 6) {
          weekEntry.weekendBooked += 1;
        } else {
          weekEntry.weekdayBooked += 1;
        }
      }
      weekMap.set(weekKey, weekEntry);
    });

    unavailableBookings.forEach((booking) => {
      if (typeof booking.amount !== "number") {
        return;
      }
      const bookingDates = getDatesInRange(booking.startDate, booking.endDate);
      if (bookingDates.length === 0) {
        return;
      }
      const amountPerDay = booking.amount / bookingDates.length;
      bookingDates.forEach((dateValue) => {
        const date = parseUtcDate(dateValue);
        const { week, weekYear } = getWeekInfo(date, weekStartDay);
        const weekKey = `${weekYear}-W${String(week).padStart(2, "0")}`;
        const weekEntry = weekMap.get(weekKey);
        if (weekEntry) {
          weekEntry.amount += amountPerDay;
        }
      });
    });

    const weeks = Array.from(weekMap.entries())
      .map(([key, entry]) => ({
        key,
        label: t("bookings.weekLabel", "Week {week}", { week: entry.week }),
        startDate: entry.startDate,
        endDate: entry.endDate,
        totalDays: entry.totalDays,
        bookedDays: entry.bookedDays,
        weekendBooked: entry.weekendBooked,
        weekdayBooked: entry.weekdayBooked,
        amount: entry.amount,
      }))
      .sort((left, right) => left.startDate.localeCompare(right.startDate));

    const monthFormatter = new Intl.DateTimeFormat(locale === "da" ? "da-DK" : "en-US", {
      month: "long",
    });
    const months = Array.from(monthMap.values())
      .map((entry) => {
        const date = new Date(Date.UTC(data.year, entry.month, 1));
        const bookedDays = entry.bookedDays;
        const availableDays = entry.totalDays - entry.bookedDays;
        const ratio = entry.totalDays === 0 ? 0 : entry.bookedDays / entry.totalDays;
        return {
          month: entry.month,
          label: monthFormatter.format(date),
          totalDays: entry.totalDays,
          bookedDays,
          availableDays,
          ratio,
        };
      })
      .sort((left, right) => left.month - right.month);

    return { weeks, months };
  }, [data, unavailableDates, weekStartDay, t, locale]);

  const availableYears = data?.years.length ? data.years : data ? [data.year] : [];
  const activeYear =
    data?.year ?? (Number.isNaN(yearParam) ? new Date().getUTCFullYear() : yearParam);

  const updateYear = (value: string) => {
    const nextParams = new URLSearchParams(search);
    nextParams.set("year", value);
    setSearch(`?${nextParams.toString()}`);
  };

  const statusClass = (status: string | null) => {
    if (!status) {
      return "bg-slate-100 text-slate-700";
    }
    if (status === "booked") {
      return "bg-emerald-100 text-emerald-700";
    }
    if (UNAVAILABLE_STATUSES.has(status)) {
      return "bg-amber-100 text-amber-700";
    }
    return "bg-slate-100 text-slate-700";
  };

  const bookingTotals = useMemo(() => {
    if (!data) {
      return {
        days: 0,
        amount: 0,
        utilities: 0,
        total: 0,
        hasAmount: false,
        hasUtilities: false,
      };
    }
    let totalDays = 0;
    let totalAmount = 0;
    let totalUtilities = 0;
    let hasAmount = false;
    let hasUtilities = false;
    data.bookings.forEach((booking) => {
      totalDays += getDatesInRange(booking.startDate, booking.endDate).length;
      if (typeof booking.amount === "number") {
        totalAmount += booking.amount;
        hasAmount = true;
      }
      if (typeof booking.utilitiesAmount === "number") {
        totalUtilities += booking.utilitiesAmount;
        hasUtilities = true;
      }
    });
    const total = totalAmount + totalUtilities;
    return {
      days: totalDays,
      amount: totalAmount,
      utilities: totalUtilities,
      total,
      hasAmount,
      hasUtilities,
    };
  }, [data]);

  const inventorySummary = useMemo(() => {
    if (!inventory) {
      return null;
    }
    const fullWeeks = inventory.fullWeeks;
    const weekends = inventory.weekends;
    const missingPrices =
      fullWeeks.filter((slot) => slot.price === null).length +
      weekends.filter((slot) => slot.price === null).length;
    const currency =
      fullWeeks.find((slot) => slot.currency)?.currency ??
      weekends.find((slot) => slot.currency)?.currency ??
      "DKK";
    return {
      fullWeeks,
      weekends,
      missingPrices,
      currency,
      totalValue: inventory.totalValue,
    };
  }, [inventory]);

  if (!houseId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("bookings.title", "Bookings")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted">
            {t("bookings.selectHouse", "Select a house to view bookings.")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-lg font-semibold text-ink">{t("bookings.title", "Bookings")}</p>
          <p className="text-sm text-muted">
            {t("bookings.subtitle", "Aggregated bookings across channels.")}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-ink" htmlFor="booking-year">
              {t("common.year", "Year")}
            </label>
            <select
              id="booking-year"
              className="h-9 rounded-md border px-3 text-sm brand-input brand-focus"
              value={String(activeYear)}
              onChange={(event) => updateYear(event.target.value)}
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          {isLoading ? (
            <p className="text-sm text-muted">{t("bookings.loading", "Loading bookings...")}</p>
          ) : null}
          {error ? <p className="text-sm text-error">{error}</p> : null}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("bookings.listTitle", "Bookings list")}</CardTitle>
        </CardHeader>
        <CardContent>
          {!data || data.bookings.length === 0 ? (
            <p className="text-sm text-muted">
              {t("bookings.empty", "No bookings for the selected year.")}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="h-8 min-w-[120px] whitespace-nowrap py-1 sm:min-w-[140px]">
                    {t("bookings.dates", "Dates")}
                  </TableHead>
                  <TableHead className="h-8 py-1">{t("bookings.days", "Days")}</TableHead>
                  <TableHead className="h-8 min-w-[110px] py-1 sm:min-w-[120px]">
                    {t("bookings.status", "Status")}
                  </TableHead>
                  <TableHead className="h-8 py-1">{t("bankTransactions.channel", "Channel")}</TableHead>
                  <TableHead className="h-8 py-1">{t("bankTransactions.amount", "Amount")}</TableHead>
                  <TableHead className="h-8 py-1">{t("bookings.utilitiesRevenue", "Utilities revenue")}</TableHead>
                  <TableHead className="h-8 py-1">{t("bookings.totalRevenue", "Total revenue")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.bookings.map((booking) => {
                  const bookingDays = getDatesInRange(booking.startDate, booking.endDate).length;
                  const utilitiesAmount =
                    typeof booking.utilitiesAmount === "number" ? booking.utilitiesAmount : null;
                  const totalAmount =
                    typeof booking.amount === "number" || typeof utilitiesAmount === "number"
                      ? (booking.amount ?? 0) + (utilitiesAmount ?? 0)
                      : null;
                  return (
                    <TableRow key={booking.id} className="h-8">
                      <TableCell className="min-w-[120px] whitespace-nowrap py-1 sm:min-w-[140px]">
                        {formatShortDate(parseUtcDate(booking.startDate))} →{" "}
                        {formatShortDate(parseUtcDate(booking.endDate))}
                      </TableCell>
                      <TableCell className="py-1">{bookingDays}</TableCell>
                      <TableCell className="py-1">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${statusClass(
                            booking.status,
                          )}`}
                        >
                          {formatStatusLabel(booking.status)}
                        </span>
                      </TableCell>
                      <TableCell className="py-1">
                        {booking.status === "booked" ? (
                          <span className="text-sm text-ink">
                            {channelLabel(
                              booking.sources.find((source) => source.status === "booked")
                                ?.channel ?? booking.sources[0]?.channel ?? "villavilla",
                            )}
                          </span>
                        ) : (
                          <div className="space-y-1 text-sm">
                            {booking.sources.map((source) => (
                              <div key={`${booking.id}-${source.channel}`}>
                                {channelLabel(source.channel)} · {formatStatusLabel(source.status)}
                              </div>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="py-1">{formatAmount(booking.amount, booking.currency)}</TableCell>
                      <TableCell className="py-1">{formatAmount(utilitiesAmount, booking.currency)}</TableCell>
                      <TableCell className="py-1">{formatAmount(totalAmount, booking.currency)}</TableCell>
                    </TableRow>
                  );
                })}
                <TableRow className="h-8">
                  <TableCell className="py-1 font-medium text-ink">
                    {t("bookings.totals", "Totals")}
                  </TableCell>
                  <TableCell className="py-1">
                    <span className="text-sm text-muted">{bookingTotals.days}</span>
                  </TableCell>
                  <TableCell className="py-1" />
                  <TableCell className="py-1" />
                  <TableCell className="py-1 font-medium text-ink">
                    {bookingTotals.hasAmount ? formatAmount(bookingTotals.amount, "DKK") : "—"}
                  </TableCell>
                  <TableCell className="py-1 font-medium text-ink">
                    {bookingTotals.hasUtilities ? formatAmount(bookingTotals.utilities, "DKK") : "—"}
                  </TableCell>
                  <TableCell className="py-1 font-medium text-ink">
                    {bookingTotals.hasAmount || bookingTotals.hasUtilities
                      ? formatAmount(bookingTotals.total, "DKK")
                      : "—"}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("bookings.weekOverview", "Week overview")}</CardTitle>
        </CardHeader>
        <CardContent>
          {!data ? (
            <p className="text-sm text-muted">
              {t("bookings.noAvailability", "No availability yet.")}
            </p>
          ) : (
            <div className="grid gap-2 md:grid-cols-2">
              {summaries.weeks.map((week) => {
                const isFullWeek = week.bookedDays > 0 && week.bookedDays === week.totalDays;
                const pills = isFullWeek
                  ? [
                      {
                        label: t("bookings.fullWeek", "Full week"),
                        className: "bg-emerald-100 text-emerald-700",
                      },
                    ]
                  : [
                      ...(week.weekdayBooked > 0
                        ? [
                            {
                              label: t("bookings.weekdays", "Weekdays"),
                              className: "bg-amber-100 text-amber-700",
                            },
                          ]
                        : []),
                      ...(week.weekendBooked > 0
                        ? [
                            {
                              label: t("bookings.weekend", "Weekend"),
                              className: "bg-slate-100 text-slate-700",
                            },
                          ]
                        : []),
                    ];
                return (
                  <div
                    key={week.key}
                    className="flex items-center justify-between rounded-md border brand-border p-3 text-sm"
                  >
                    <div>
                      <p className="font-medium text-ink">{week.label}</p>
                      <p className="text-xs text-muted">
                        {formatShortDate(parseUtcDate(week.startDate))} →{" "}
                        {formatShortDate(parseUtcDate(week.endDate))}
                      </p>
                      <p className="text-xs text-muted">
                        {t("bookings.daysBooked", "{count} days booked", {
                          count: week.bookedDays,
                        })}
                      </p>
                      <p className="text-xs text-muted">{formatAmount(week.amount, "DKK")}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {pills.length ? (
                        <div className="flex flex-wrap justify-end gap-2">
                          {pills.map((pill) => (
                            <span
                              key={`${week.key}-${pill.label}`}
                              className={`rounded-full px-2 py-1 text-xs font-medium ${pill.className}`}
                            >
                              {pill.label}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-muted">
                          {t("bookings.noBookings", "No bookings")}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("bookings.monthlyCoverage", "Monthly coverage")}</CardTitle>
        </CardHeader>
        <CardContent>
          {!data ? (
            <p className="text-sm text-muted">
              {t("bookings.noMonthlyCoverage", "No monthly coverage yet.")}
            </p>
          ) : (
            <div className="grid gap-3 md:grid-cols-3">
              {summaries.months.map((month) => (
                <div
                  key={month.month}
                  className="rounded-md border brand-border p-3 text-sm"
                  style={{ backgroundColor: getMonthColor(month.ratio) }}
                >
                  <p className="font-medium text-ink">{month.label}</p>
                  <p className="text-xs text-muted">
                    {t("bookings.monthSummary", "{booked} days booked - {available} left", {
                      booked: month.bookedDays,
                      available: month.availableDays,
                    })}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("bookings.inventoryTitle", "Inventory / Unbooked")}</CardTitle>
        </CardHeader>
        <CardContent>
          {inventoryLoading ? (
            <p className="text-sm text-muted">
              {t("bookings.inventoryLoading", "Loading inventory...")}
            </p>
          ) : null}
          {inventoryError ? <p className="text-sm text-error">{inventoryError}</p> : null}
          {!inventorySummary ? (
            <p className="text-sm text-muted">
              {t("bookings.noInventory", "No inventory data yet.")}
            </p>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-6 text-sm">
                <div>
                  <p className="text-xs text-muted">
                    {t("bookings.fullWeeksLeft", "Full weeks left")}
                  </p>
                  <p className="font-medium text-ink">{inventorySummary.fullWeeks.length}</p>
                </div>
                <div>
                  <p className="text-xs text-muted">
                    {t("bookings.weekendsLeft", "Weekends left")}
                  </p>
                  <p className="font-medium text-ink">{inventorySummary.weekends.length}</p>
                </div>
                <div>
                  <p className="text-xs text-muted">{t("bookings.totalValue", "Total value")}</p>
                  <p className="font-medium text-ink">
                    {formatAmount(inventorySummary.totalValue, inventorySummary.currency)}
                  </p>
                </div>
              </div>
              {inventorySummary.missingPrices ? (
                <p className="text-xs text-muted">
                  {t("bookings.missingPrices", "Missing prices for {count} slots.", {
                    count: inventorySummary.missingPrices,
                  })}
                </p>
              ) : null}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-ink">
                    {t("bookings.fullWeeks", "Full weeks")}
                  </p>
                  {inventorySummary.fullWeeks.length === 0 ? (
                    <p className="text-sm text-muted">
                      {t("bookings.noFullWeeks", "No full weeks left.")}
                    </p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("bookings.period", "Period")}</TableHead>
                          <TableHead>{t("bookings.week", "Week")}</TableHead>
                          <TableHead>{t("bookings.price", "Price")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {inventorySummary.fullWeeks.map((slot) => (
                          <TableRow key={`${slot.kind}-${slot.startDate}`}>
                            <TableCell>
                              {formatShortDate(parseUtcDate(slot.startDate))} →{" "}
                              {formatShortDate(parseUtcDate(slot.endDate))}
                            </TableCell>
                            <TableCell>
                              {slot.week ? t("bookings.weekValue", "W{week}", { week: slot.week }) : "—"}
                            </TableCell>
                            <TableCell>
                              {slot.price === null
                                ? "—"
                                : formatAmount(slot.price, slot.currency ?? inventorySummary.currency)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-ink">
                    {t("bookings.weekends", "Weekends")}
                  </p>
                  {inventorySummary.weekends.length === 0 ? (
                    <p className="text-sm text-muted">
                      {t("bookings.noWeekends", "No weekends left.")}
                    </p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("bookings.period", "Period")}</TableHead>
                          <TableHead>{t("bookings.week", "Week")}</TableHead>
                          <TableHead>{t("bookings.price", "Price")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {inventorySummary.weekends.map((slot) => (
                          <TableRow key={`${slot.kind}-${slot.startDate}`}>
                            <TableCell>
                              {formatShortDate(parseUtcDate(slot.startDate))} →{" "}
                              {formatShortDate(parseUtcDate(slot.endDate))}
                            </TableCell>
                            <TableCell>
                              {slot.week ? t("bookings.weekValue", "W{week}", { week: slot.week }) : "—"}
                            </TableCell>
                            <TableCell>
                              {slot.price === null
                                ? "—"
                                : formatAmount(slot.price, slot.currency ?? inventorySummary.currency)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
